import { Dropdown } from 'semantic-ui-react';

/**
 * O Dropdown do semantic-ui-react (0.88) registra seus atalhos de teclado no
 * `document` em fase de captura enquanto `state.focus` for `true`, e o handler
 * `openOnSpace` chama `preventDefault()` no Espaço sem verificar de onde o evento
 * veio. Quando um Dropdown fica com o foco preso — `handleBlur` é ignorado se
 * `isMouseDown` não tiver sido zerado por um `mouseup` que nunca chegou ao
 * document — ele passa a engolir o Espaço de qualquer campo de texto da página,
 * inclusive do editor Quill, até que a página seja recarregada.
 *
 * O guard mantém o comportamento original do Dropdown e apenas ignora eventos de
 * teclado originados em campos de texto que não pertencem a ele.
 */

const GUARDED_HANDLERS = [
  'openOnSpace',
  'openOnArrow',
  'selectItemOnEnter',
  'moveSelectionOnKeyDown',
  'removeItemOnBackspace'
];

type GuardedHandler = ((event: KeyboardEvent) => void) & { __lbKeyboardGuard?: boolean };

type SemanticDropdownInstance = {
  ref?: { current: HTMLElement | null };
  render: () => unknown;
  [internal: string]: unknown;
};

const NON_TEXT_INPUT_TYPES = [
  'button',
  'checkbox',
  'color',
  'file',
  'image',
  'radio',
  'range',
  'reset',
  'submit'
];

const isTextEntryElement = (node: EventTarget | null): node is HTMLElement => {
  if (!(node instanceof HTMLElement)) return false;
  if (node instanceof HTMLTextAreaElement) return true;
  if (node instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.includes(node.type);
  }
  if (node.isContentEditable) return true;
  return !!node.closest('[contenteditable=""],[contenteditable="true"]');
};

const comesFromForeignTextField = (
  instance: SemanticDropdownInstance,
  event: KeyboardEvent
) => {
  if (!isTextEntryElement(event.target)) return false;
  const dropdownNode = instance.ref ? instance.ref.current : null;
  return !dropdownNode || !dropdownNode.contains(event.target);
};

const guardKeyboardHandlers = (instance: SemanticDropdownInstance) => {
  GUARDED_HANDLERS.forEach(name => {
    const handler = instance[name] as GuardedHandler;
    if (typeof handler !== 'function' || handler.__lbKeyboardGuard) return;

    const guarded: GuardedHandler = (event: KeyboardEvent) => {
      if (comesFromForeignTextField(instance, event)) return;
      handler(event);
    };
    guarded.__lbKeyboardGuard = true;
    instance[name] = guarded;
  });
};

let installed = false;

export const installSemanticDropdownKeyboardGuard = () => {
  if (installed) return;
  installed = true;

  const dropdownPrototype = (Dropdown.prototype as unknown) as SemanticDropdownInstance;
  const originalRender = dropdownPrototype.render;

  // O patch precisa acontecer antes do render porque é ele que passa os handlers
  // para o EventStack que os registra no document.
  dropdownPrototype.render = function render(this: SemanticDropdownInstance) {
    guardKeyboardHandlers(this);
    return originalRender.call(this);
  };
};
